<template>
    <div class="gb-index-fields-wrapper">
        <tabs :value="selectedFilterTab" @update:value="selectFilterTab">
            <tab-list>
                <tab value="parameter" :disabled="props.readOnly">Filter by parameters</tab>
                <tab value="plant" :disabled="props.readOnly">Filter by plant</tab>
                <tab v-if="props.allowBenchmarkPool" value="pool" :disabled="props.readOnly">Benchmark pool</tab>
            </tab-list>
        </tabs>
        <div class="gb-index-fields-content">
            <benchmark-pool-selector
                v-if="selectedFilterTab === 'pool'"
                ref="poolSelector"
                management-context="index"
                :model-value="customIndex.benchmarkPoolId"
                :scope="customIndex.benchmarkPoolScope"
                :disabled="props.readOnly"
                @update:model-value="updatePool"
                @update:scope="updatePoolScope"
                @update:validation="poolValidation = $event"
            />
            <template v-else-if="isPlantFilter">
                <div class="option">
                    Selected plants
                    <plant-selector :selected-plant-ids="customIndex.selectedPlants" :disabled="props.readOnly" @update:model-value-multiple="updatePlants" multiSelect />
                </div>
            </template>
            <template v-else>
                <div class="option">
                    Fuel Type
                    <fuel-selector
                        :model-value="processSelectedFuels(customIndex.selectedFuel)"
                        multi-select
                        :disabled="props.readOnly"
                        @update:model-value-multiple="updateFuels"
                    />
                </div>
                <div class="option">
                    Zone
                    <spark-multi-select
                        :model-value="customIndex.selectedZone"
                        option-label="name"
                        :disabled="props.readOnly"
                        :options="zoneOptions"
                        @update:model-value="updateZones"
                    />
                </div>
                <div class="option">
                    BM Status
                    <spark-multi-select
                        :model-value="customIndex.selectedBmStatus"
                        option-label="name"
                        :disabled="props.readOnly"
                        :options="BM_STATUS_OPTIONS"
                        @update:model-value="updateBmStatus"
                    />
                </div>
            </template>
            <template v-if="!isPlantFilter && selectedFilterTab !== 'pool'">
                <div class="option">
                    Owner
                    <spark-multi-select
                        :model-value="customIndex.selectedOwners"
                        option-label="name"
                        :disabled="props.readOnly"
                        :options="plantOwnerOptions"
                        @update:model-value="updateOwners"
                    />
                </div>
                <div class="option">
                    Optimiser
                    <div>
                        <spark-multi-select
                            class="w-100"
                            :model-value="customIndex.selectedOptimisers"
                            :disabled="!fuelTypeContainsBattery || props.readOnly"
                            option-label="name"
                            :options="batteryOptimiserOptions"
                            @update:model-value="updateOptimisers"
                        />
                        <hover-tooltip v-if="!fuelTypeContainsBattery" :tooltip-text="BATTERY_ONLY_WARNING_TEXT" />
                    </div>
                </div>
                <div class="option">
                    Capacity (MW)
                    <min-max
                        :disabled="props.readOnly"
                        :model-value="capacityValue"
                        :min="0"
                        :max="2000"
                        :default-value="defaultCapacity"
                        id="capacity"
                        @update:model-value="updateCapacity"
                    />
                </div>
                <div />
                <div class="option">
                    Duration (Hours)
                    <div>
                        <min-max
                            :model-value="durationValue"
                            :disabled="!fuelTypeContainsBattery || props.readOnly"
                            :min="0"
                            :max="10"
                            :default-value="defaultDuration"
                            @update:model-value="updateDuration"
                        />
                        <hover-tooltip v-if="!fuelTypeContainsBattery" :tooltip-text="BATTERY_ONLY_WARNING_TEXT" />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import PlantSelector from '@/components/powerMarkets/selectors/PlantSelector.vue';
import { IFuelType } from '@/interfaces/powerMarkets/IFuelType.ts';
import SparkMultiSelect from '@/components/common/SparkMultiSelect.vue';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { IMinMax } from '@/interfaces/general/IMinMax.ts';
import { IGbIndex } from '@/interfaces/benchmarks/index/IIndex.ts';
import { zoneOptions } from '@/constants/ZoneOptions.ts';
import { IPlant } from '@/interfaces/powerMarkets/plant/IPlant.ts';
import { isArrayEmpty } from '@/utils/isArrayEmpty.ts';
import { CustomIndexEditorEnum } from '@/enums/benchmarking/IndexEnums.ts';
import MinMax from '@/components/powerMarkets/input/MinMax.vue';
import FuelSelector from '@/components/powerMarkets/selectors/FuelSelector.vue';
import { DEFAULT_GB_INDEX_FIELDS, BATTERY_ONLY_WARNING_TEXT, BM_STATUS_OPTIONS } from '@/constants/benchmarks/IndexConstants.ts';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import HoverTooltip from '@/components/powerMarkets/common/HoverTooltip.vue';
import BenchmarkPoolSelector from '@/components/powerMarkets/BenchmarkPoolSelector.vue';
import type { IBenchmarkPoolScope } from '@/interfaces/powerMarkets/leaderboard/IBenchmarkPool.ts';

const defaultCapacity = { min: DEFAULT_GB_INDEX_FIELDS.selectedCapacity[0], max: DEFAULT_GB_INDEX_FIELDS.selectedCapacity[1] };
const defaultDuration = { min: DEFAULT_GB_INDEX_FIELDS.selectedDuration[0], max: DEFAULT_GB_INDEX_FIELDS.selectedDuration[1] };

const dataStore = useDataStore();

const props = defineProps<{
    indexSettings: IGbIndex;
    readOnly: boolean;
    editorType: CustomIndexEditorEnum;
    allowBenchmarkPool?: boolean;
}>();

const emit = defineEmits<{
    'update:index': [customIndexSettings: IGbIndex];
    'update:validation': [validationString: string | null];
}>();

const customIndex = ref<IGbIndex>(props.indexSettings);
const selectedFilterTab = ref<string>(processSelectedFilterTab(props.indexSettings));
const capacityValue = ref<IMinMax>(processCapacityValue(props.indexSettings));
const durationValue = ref<IMinMax>(processDurationValue(props.indexSettings));
const poolSelector = ref<InstanceType<typeof BenchmarkPoolSelector> | null>(null);
const poolValidation = ref<string | null>('Please select a saved benchmark pool.');

function updatePool (poolId: string | null): void {
    if (props.readOnly) return;
    if (customIndex.value.benchmarkPoolId !== poolId) delete customIndex.value.benchmarkPoolScope;
    customIndex.value.benchmarkPoolId = poolId ?? undefined;
    delete customIndex.value.id;
    emit('update:index', customIndex.value);
}

function updatePoolScope (scope: IBenchmarkPoolScope | undefined): void {
    if (props.readOnly) return;
    customIndex.value.benchmarkPoolScope = scope;
    delete customIndex.value.id;
    emit('update:index', customIndex.value);
}

async function requestLeave (): Promise<boolean> {
    return await poolSelector.value?.requestLeave() ?? true;
}
defineExpose({ requestLeave });

const isPlantFilter = computed<boolean>(() => selectedFilterTab.value === 'plant');
const fuelTypeContainsBattery = computed(() => customIndex.value.selectedFuel.some(fuel => fuel.id === 'Battery'));

const batteryOptimiserOptions = computed<Array<IIdName>>(() => {
    if (!dataStore.nonOwnerPlants) return [];
    const distinctOptimisers = dataStore.nonOwnerPlants
        .filter(plant => plant.searchable === true && plant.fuel === 'Battery' && plant.optimiser !== null)
        .map(plant => ({ id: plant.optimiser || '', name: plant.optimiser || '' }))
        .filter((value, index, self) => self.findIndex(v => v.id === value.id) === index)
        .filter(optimisers => optimisers.id !== '');
    return [{ id: 'All Optimisers', name: 'All Optimisers' }, ...distinctOptimisers];
});

const plantOwnerOptions = computed<Array<IIdName>>(() => {
    if (!dataStore.nonOwnerPlants) return [];
    const distinctOwners = dataStore.nonOwnerPlants
        .filter(plant => plant.searchable === true && plant.owner !== null)
        .map(plant => ({ id: plant.owner || '', name: plant.owner || '' }))
        .filter((value, index, self) => self.findIndex(v => v.id === value.id) === index)
        .filter(owners => owners.id !== '');
    return [{ id: 'All Owners', name: 'All Owners' }, ...distinctOwners];
});

const validationString = computed<string | null>(() => {
    if (selectedFilterTab.value === 'pool') {
        return poolValidation.value;
    }
    if (isPlantFilter.value) {
        if (isArrayEmpty(customIndex.value.selectedPlants)) {
            return 'Please select at least one plant.';
        }
    } else {
        if (isArrayEmpty(customIndex.value.selectedFuel)
            || isArrayEmpty(customIndex.value.selectedZone)
            || isArrayEmpty(customIndex.value.selectedBmStatus)
            || isArrayEmpty(customIndex.value.selectedOwners)) {
            return 'Please select all required options.';
        }
        if (customIndex.value.selectedFuel.some(fuel => fuel.id === 'Battery')
            && isArrayEmpty(customIndex.value.selectedOptimisers)) {
            return 'Please select optimisers for Battery fuel type.';
        }
    }
    return null;
});

function processSelectedFilterTab (indexSettings: IGbIndex): string {
    if (props.allowBenchmarkPool && indexSettings.benchmarkPoolId) return 'pool';
    return indexSettings.selectedPlants.length > 0 ? 'plant' : 'parameter';
}

function processCapacityValue (indexSettings: IGbIndex): IMinMax {
    return Array.isArray(indexSettings.selectedCapacity)
        ? { min: indexSettings.selectedCapacity[0], max: indexSettings.selectedCapacity[1] }
        : defaultCapacity;
}

function processDurationValue (indexSettings: IGbIndex): IMinMax {
    return Array.isArray(indexSettings.selectedDuration)
        ? { min: indexSettings.selectedDuration[0], max: indexSettings.selectedDuration[1] }
        : defaultDuration;
}

function processSelectedFuels (selectedFuels: Array<IIdName>): Array<string> | null {
    if (!selectedFuels.length) return null;
    return selectedFuels.map(fuel => fuel.id);
}

async function selectFilterTab (tab: string | number): Promise<void> {
    if (props.readOnly || tab === selectedFilterTab.value) return;
    if (!(await requestLeave())) return;
    selectedFilterTab.value = String(tab);
    customIndex.value.selectedPlants = [];
    delete customIndex.value.id;
    if (selectedFilterTab.value !== 'pool') {
        delete customIndex.value.benchmarkPoolId;
        delete customIndex.value.benchmarkPoolScope;
    }
    emit('update:index', customIndex.value);
}

function updatePlants (newPlants: Array<IPlant>): void {
    if (props.readOnly) return;
    customIndex.value.selectedPlants = newPlants.map(plant => plant.id);
    emit('update:index', customIndex.value);
}

function updateFuels (newFuels: Array<IFuelType>): void {
    if (props.readOnly) return;
    customIndex.value.selectedFuel = newFuels.map(fuel => ({ id: fuel.id, name: fuel.name }));
    emit('update:index', customIndex.value);
}

function resolveExclusiveAllSelection (
    current: Array<IIdName>,
    next: Array<IIdName>,
    allOptionId: string,
): Array<IIdName> {
    const currentHasAll = current.some(option => option.id === allOptionId);
    const nextHasAll = next.some(option => option.id === allOptionId);
    if (currentHasAll && nextHasAll && next.length > 1) {
        return next.filter(option => option.id !== allOptionId);
    }
    if (!currentHasAll && nextHasAll) {
        const allOption = next.find(option => option.id === allOptionId);
        return allOption ? [allOption] : next;
    }
    return next;
}

function updateZones (newZones: Array<IIdName>): void {
    if (props.readOnly) return;
    customIndex.value.selectedZone = resolveExclusiveAllSelection(customIndex.value.selectedZone, newZones, 'All Zones');
    emit('update:index', customIndex.value);
}

function updateBmStatus (newBmStatus: Array<IIdName>): void {
    if (props.readOnly) return;
    customIndex.value.selectedBmStatus = resolveExclusiveAllSelection(customIndex.value.selectedBmStatus, newBmStatus, 'All');
    emit('update:index', customIndex.value);
}

function updateOwners (newOwners: Array<IIdName>): void {
    if (props.readOnly) return;
    customIndex.value.selectedOwners = resolveExclusiveAllSelection(customIndex.value.selectedOwners, newOwners, 'All Owners');
    emit('update:index', customIndex.value);
}

function updateOptimisers (newOptimisers: Array<IIdName>): void {
    if (props.readOnly) return;
    customIndex.value.selectedOptimisers = resolveExclusiveAllSelection(
        customIndex.value.selectedOptimisers,
        newOptimisers,
        'All Optimisers',
    );
    emit('update:index', customIndex.value);
}

function updateCapacity (newCapacity: IMinMax): void {
    if (props.readOnly) return;
    capacityValue.value = newCapacity;
    customIndex.value.selectedCapacity = [newCapacity.min ?? defaultCapacity.min, newCapacity.max ?? defaultCapacity.max];
    emit('update:index', customIndex.value);
}

function updateDuration (newDuration: IMinMax): void {
    if (props.readOnly) return;
    durationValue.value = newDuration;
    customIndex.value.selectedDuration = [newDuration.min ?? defaultDuration.min, newDuration.max ?? defaultDuration.max];
    emit('update:index', customIndex.value);
}

watch(() => props.indexSettings, (newSettings) => {
    if (JSON.stringify(customIndex.value) === JSON.stringify(newSettings)) return;
    customIndex.value = newSettings;
    selectedFilterTab.value = processSelectedFilterTab(newSettings);
    capacityValue.value = processCapacityValue(newSettings);
    durationValue.value = processDurationValue(newSettings);
}, { deep: true });

watch(() => validationString.value, (newVal) => {
    emit('update:validation', newVal);
}, { immediate: true });

</script>

<style lang="scss" scoped>
.gb-index-fields-wrapper {
    display: grid;
    grid-template-rows: auto 1fr;
    gap: var(--extraLargeSpacing);
    height: 100%;
    overflow: hidden;
    padding: var(--extraLargeSpacing);
}
.gb-index-fields-content {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--largeSpacing) calc(2 * var(--extraLargeSpacing));
    height: 100%;
    overflow: auto;
}
.option {
    display: flex;
    flex-direction: column;
    gap: var(--smallSpacing);
    min-width: 0;
}
.checkbox-container {
    display: flex;
    align-items: center;
    gap: var(--defaultSpacing);
    padding: var(--smallSpacing) 0;
}
</style>
